import { useLocation } from "react-router";
import { useRecordsViewOptions } from "@/state/recordsView";
import { useBreakpoint } from "@/common/hooks";
import { useAppSelector } from "@/state/redux/hooks";
import { selectMe, selectViewingRecord } from "@/state/redux/slices/ui";

/**
 * Which parts of the app frame a route actually wants.
 *
 * Both sidebars and the header's interval are answers to "when is this?", and
 * two places have no such question. A single record's page is one: it shows
 * one record, reached by name, and neither the time hierarchy nor the
 * collection list has anything to do while it is on screen. A collection's
 * notes tab is the other: note records carry no interval at all, so a time
 * cursor over them moves nothing.
 *
 * Decided in one place because `App` (which mounts the panels) and `Header`
 * (which mounts the controls that open them) sit on opposite sides of the
 * `Outlet` and must not disagree — a header button that toggles a panel which
 * is not rendered does nothing at all.
 *
 * Read off the pathname rather than `useParams`: `App` is the layout route's
 * element, so its RouteContext stops at "/" and the child's `:userID` and
 * `:collectionID` never reach its params.
 *
 * One case is not a fact about the pathname at all. "/" signed out is the
 * landing page (see `Home`), which sells the app rather than showing anyone's
 * records, and every panel there would be an empty answer to a question the
 * visitor has not asked yet. Both callers sit inside the Redux `Provider`, so
 * reading `me` here is safe; and nothing renders until the session lookup has
 * resolved (`main.tsx`), so this never flips under a mounted page.
 */

/** First segments that are routes in their own right, not a `:userID`. */
const RESERVED = new Set(["admin", "login", "record"]);

function isRecordPath(parts: string[]) {
  // The legacy permalink, and the readable form with its reserved "-" segment.
  return (
    (parts[0] === "record" && parts.length === 2) ||
    (parts.length === 3 && parts[1] === "-")
  );
}

function isCollectionPath(parts: string[]) {
  return parts.length === 2 && !RESERVED.has(parts[0]);
}

export interface AppChrome {
  /** A single record's page, under either of its two URLs. */
  isRecordPage: boolean;
  /** "/" with no session: the marketing page, not anybody's timeline. */
  isLanding: boolean;
  /** `:userID` from the readable record URL; absent on the legacy permalink. */
  recordUserRef?: string;
  /** `:recordID` from the legacy permalink; absent on the readable URL. */
  recordID?: string;
  /** Mount the time sidebar, and with it the ⌘B shortcut and the swipe. */
  showTimeSidebar: boolean;
  /** Mount the collections sidebar, and with it the ⌘J shortcut. */
  showCollectionsSidebar: boolean;
  /** Name the current interval in the header. */
  showInterval: boolean;
  /** Mount the header at all. */
  showHeader: boolean;
}

export function useAppChrome(): AppChrome {
  const { pathname } = useLocation();
  const { options } = useRecordsViewOptions();

  const me = useAppSelector(selectMe);

  const parts = pathname.split("/").filter(Boolean);
  const isRecordPage = isRecordPath(parts);
  const isLanding = parts.length === 0 && !me;
  // The same fallback `Records` applies: a stored "notes" mode means nothing
  // outside a collection, and there the timeline is what is on screen.
  const notesMode = options.mode === "notes" && isCollectionPath(parts);
  // Routes that are neither a list nor a record, so there is no interval to
  // name — but the panels stay, as the way back out of them.
  const bare = parts[0] === "admin" || parts[0] === "login";
  // Below the split layout's breakpoint the preview is not beside the list but
  // over it, and on a phone that leaves the header as the only thing between a
  // record and the top of the screen -- naming a time the record being read is
  // not filed under. It has its own close button, so nothing is stranded.
  // Guarded by route because `viewingRecord` outlives the list it was opened
  // from: it survives anywhere in the collection's scope, and a header page
  // reached from there must not lose its header to a preview it never shows.
  const viewingRecord = useAppSelector(selectViewingRecord);
  const { isMd } = useBreakpoint();
  const previewOverList = !isMd && !!viewingRecord && !isRecordPage && !bare;

  return {
    isRecordPage,
    isLanding,
    recordUserRef: isRecordPage && parts[1] === "-" ? parts[0] : undefined,
    recordID: isRecordPage && parts[0] === "record" ? parts[1] : undefined,
    showTimeSidebar: !isRecordPage && !notesMode && !isLanding,
    showCollectionsSidebar: !isRecordPage && !isLanding,
    showInterval: !isRecordPage && !notesMode && !bare && !isLanding,
    showHeader: !previewOverList,
  };
}
